import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const { user, ready } = useAuth();
  const [ids, setIds] = useState([]);
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setIds([]);
      setProducts([]);
      setLoaded(true);
      return;
    }
    try {
      const { data } = await api.get("/wishlist");
      setIds(data.product_ids || []);
      setProducts(data.products || []);
    } catch (e) {
      setIds([]);
      setProducts([]);
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    if (!ready) return;
    refresh();
  }, [ready, refresh]);

  const toggle = async (productId) => {
    if (!user) {
      return { requiresAuth: true };
    }
    const { data } = await api.post("/wishlist/toggle", { product_id: productId });
    // Optimistic-ish: refetch
    await refresh();
    return { in_wishlist: data.in_wishlist };
  };

  const has = (id) => ids.includes(id);

  return (
    <WishlistContext.Provider value={{ ids, products, has, toggle, refresh, loaded, count: ids.length }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error("useWishlist must be inside WishlistProvider");
  return ctx;
}
